import { showHttpError } from '@calendly/alert'
import { parseQuery } from '@calendly/http-tools'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { omitBy, isNil } from 'lodash-es'

import { setGetParams } from 'actions/core/navigation'

import {
  getMigrations,
} from '../api'
import {
  paginationSelector,
  filterTermSelector,
  sortFieldSelector,
  sortOrderSelector,
} from './selectors'

export const fetchMigrations = createAsyncThunk(
  'usersPage/fetchMigrations',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const page = paginationSelector(state).currentPage
    const filter = filterTermSelector(state)
    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)

    const [success, response] = await getMigrations(
      omitBy({ page, filter_term: filter, sort_field: sortField, sort_order: sortOrder }, isNil)
    )
    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

const initialState = {
  migrations: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  },
  filter: '',
  sortField: 'email',
  sortOrder: 'asc',
  isLoading: true,
  selectedMigrations: [],
}

const slice = createSlice({
  name: 'usersPage/migrating',
  initialState,
  reducers: {
    resetSelection: (state, { payload: migrations }) => {
      const { selectedMigrations } = state
      const ids = migrations.map(({ id }) => id)
      state.selectedMigrations = selectedMigrations.filter(({ id }) => !ids.includes(id))
    },
    addSelectedMigrations: (state, { payload: migrations }) => {
      state.selectedMigrations.push(...migrations)
    },
    setPage: (state, { payload }) => {
      state.pagination.currentPage = payload
    },
    setFilter: (state, { payload }) => {
      state.filter = payload
    },
    setSortOrder(state, { payload }) {
      state.sortOrder = payload
    },
    setSortField(state, { payload }) {
      state.sortField = payload
    },
    sortBy(state, { payload }) {
      if (state.sortField === payload) {
        state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        state.sortOrder = 'asc'
      }

      state.sortField = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMigrations.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchMigrations.fulfilled, (state, { payload }) => {
      state.migrations = payload.results
      state.pagination = {
        currentPage: payload.pagination.current_page,
        totalPages: payload.pagination.total_pages,
        totalCount: payload.pagination.total_count,
        pageSize: payload.pagination.page_size
      }
      state.isLoading = false
    })
    builder.addCase(fetchMigrations.rejected, (state) => {
      state.isLoading = false
    })
  }
})

export const updatePage = ({ page }) => (dispatch) => {
  dispatch(setGetParams({ page }))
  dispatch(slice.actions.setPage(page))
  dispatch(fetchMigrations())
}

export const initPage = () => async (dispatch) => {
  const params = parseQuery(window.location.search)
  const page = params.page ? parseInt(params.page, 10) : 1
  dispatch(slice.actions.setPage(page))

  if (params.sort_field) dispatch(slice.actions.setSortField(params.sort_field))
  if (params.sort_order) dispatch(slice.actions.setSortOrder(params.sort_order))

  const { error, payload: results } = await dispatch(fetchMigrations())

  if (error) return

  if (results.pagination.is_out_of_range) {
    await dispatch(updatePage({ page: 1 }))
  }
}

export const search = (query) => (dispatch) => {
  dispatch(slice.actions.setFilter(query))
  dispatch(slice.actions.setPage(1))
  dispatch(setGetParams({ page: null }))
  dispatch(fetchMigrations())
}

export const sortMigrations = (field) => async (dispatch, getState) => {
  dispatch(slice.actions.sortBy(field))
  dispatch(slice.actions.setPage(1))
  const state = getState()
  const sortOrder = sortOrderSelector(state)
  const sortField = sortFieldSelector(state)
  dispatch(setGetParams({ sort_field: sortField, sort_order: sortOrder, page: null }))
  await dispatch(fetchMigrations())
}

export const { reducer } = slice
